// OrderDetailDesktop.tsx
import React, { useMemo } from "react";
import { Button, Card, Space, Steps, Table, Typography } from "antd";
import { FileAddFilled } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { OrderDetail, OrderItem } from "../model/orderDetail.ui";
import { formatDate, STATUS_TAGS as STATUS_ORDER } from "../../../utils/constants";
import { ButtonCustom } from "../../../components/ButtonCustom";

const { Title, Text } = Typography;

interface OrderDetailDesktopProps {
    orderDetail?: OrderDetail;
    currentStep: number;
    totalAmount: number;
    handleDownloadFile: () => void;
    handleSave: () => void;
    handleSendOrder: () => void;
}

export const OrderDetailDesktop: React.FC<OrderDetailDesktopProps> = ({
    orderDetail,
    currentStep,
    totalAmount,
    handleDownloadFile,
    handleSave,
    handleSendOrder,
}) => {
    // Lấy danh sách các ngày giao hàng duy nhất từ tất cả sản phẩm
    // const scheduleDates = useMemo(() => {
    //     if (!orderDetail?.order_items) return [];
    //     const datesSet = new Set<string>();
    //     orderDetail.order_items.forEach((item) => {
    //         item.schedules?.forEach((s: string) => {
    //             if (s.delivery_date) datesSet.add(formatDate(s.delivery_date));
    //         });
    //     });
    //     return Array.from(datesSet).sort();
    // }, [orderDetail]);

    // Dựng cấu trúc cột cho bảng Ant Design
    // const columns: ColumnsType<OrderItem> = useMemo(() => {
    //     const baseColumns: ColumnsType<OrderItem> = [
    //         {
    //             title: "Sản phẩm",
    //             dataIndex: "product_name",
    //             key: "product_name",
    //             render: (text) => <Text strong>{text || "-"}</Text>,
    //         },
    //         {
    //             title: "Nơi sản xuất",
    //             dataIndex: "origin",
    //             key: "origin",
    //             render: (text) => text || "-",
    //         },
    //         {
    //             title: "Đơn giá",
    //             dataIndex: "proposed_price",
    //             key: "proposed_price",
    //             render: (price) =>
    //                 price ? `¥${Number(price).toLocaleString()}` : "-",
    //         },
    //     ];

    //     // Cột ngày giao hàng động (01/09, 02/09, ...)
    //     // const dateColumns: ColumnsType<OrderItem> = scheduleDates.map((dateStr) => ({
    //     //     title: dateStr,
    //     //     key: dateStr,
    //     //     align: "center",
    //     //     render: (_, record) => {
    //     //         const schedule = record.schedules?.find(
    //     //             (s: any) => s.delivery_date === dateStr
    //     //         );
    //     //         return schedule ? schedule.quantity : 0;
    //     //     },
    //     // }));

    //     const summaryColumns: ColumnsType<OrderItem> = [
    //         {
    //             title: "Tổng số lượng",
    //             dataIndex: "order_quantity",
    //             key: "order_quantity",
    //             align: "right",
    //             render: (qty) => (
    //                 <Text strong>{qty ? `${qty} kg` : "0 kg"}</Text>
    //             ),
    //         },
    //         {
    //             title: "Thành tiền ước tính",
    //             key: "subtotal",
    //             align: "right",
    //             render: (_, record) => {
    //                 const subtotal =
    //                     Number(record.proposed_price ?? 0) *
    //                     Number(record.order_quantity ?? 0);
    //                 return (
    //                     <Text strong style={{ color: "#111827" }}>
    //                         ¥{subtotal.toLocaleString()}
    //                     </Text>
    //                 );
    //             },
    //         },
    //     ];

    //     return [...baseColumns, ...dateColumns, ...summaryColumns];
    // }, [scheduleDates]);

    // Tính tổng số lượng hàng toàn đơn
    const totalQuantitySum = useMemo(() => {
        return (
            orderDetail?.order_items?.reduce(
                (acc, item) => acc + Number(item.order_quantity ?? 0),
                0
            ) || 0
        );
    }, [orderDetail]);

    return (
        <div style={{ margin: "0 auto", padding: "24px 16px" }}>
            {/* Thông tin đơn hàng gốc */}
            <Card
                style={{
                    borderRadius: 16,
                    marginBottom: 16,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
            >
                <Space orientation="vertical" size={6} style={{ width: "100%" }}>
                    <Title level={4} style={{ margin: 0 }}>
                        注文番号: {orderDetail?.order_number || "N/A"} {/* Mã đơn hàng */}
                    </Title>
                    <Text type="secondary">
                        元提案: {/* Đề xuất gốc */} <Text strong>{orderDetail?.order_number || "N/A"}</Text>
                    </Text>
                    <Text type="secondary">
                        顧客名: {/* Khách hàng */}{" "}
                        <Text strong>{orderDetail?.customer?.company_in_name || "N/A"}</Text>
                    </Text>
                </Space>
            </Card>

            {/* Thanh trạng thái quy trình */}
            <Card
                style={{
                    borderRadius: 16,
                    marginBottom: 20,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
            >
                <Text
                    style={{
                        fontSize: 16,
                        fontWeight: "bold",
                        display: "block",
                        marginBottom: 16,
                    }}
                >
                    注文ステータス {/* Trạng thái đơn hàng */}
                </Text>
                <Steps
                    current={currentStep}
                    items={[
                        { title: STATUS_ORDER.pending.label },
                        { title: STATUS_ORDER.waiting_final_confirmation.label },
                        { title: STATUS_ORDER.confirmed.label },
                        { title: "完了" /* Hoàn tất đơn */ },
                    ]}
                />
            </Card>

            {/* Bảng chi tiết lịch trình & số lượng */}
            <Card
                style={{
                    borderRadius: 16,
                    marginBottom: 24,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
                styles={{ body: { padding: 24 } }}
            >
                <Title
                    level={5}
                    style={{ margin: "0 0 16px 0", fontSize: 16, fontWeight: 700 }}
                >
                    供給スケジュール＆数量一覧 {/* Bảng kê lịch trình & số lượng cung ứng */}
                </Title>

                {/* <Table
                    columns={columns}
                    dataSource={orderDetail?.order_items || []}
                    rowKey={(record) => record.id || Math.random()}
                    pagination={false}
                    scroll={{ x: "max-content" }}
                    bordered={false}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row style={{ backgroundColor: "#F8FAFC" }}>
                                <Table.Summary.Cell index={0} colSpan={3 + scheduleDates.length}>
                                    <Text strong style={{ fontSize: 15 }}>
                                        TỔNG CỘNG
                                    </Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                    <Text strong style={{ fontSize: 15 }}>
                                        {totalQuantitySum.toLocaleString()} kg
                                    </Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2} align="right">
                                    <Text strong style={{ color: "#2563EB", fontSize: 16 }}>
                                        ¥{totalAmount.toLocaleString()}
                                    </Text>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                /> */}
            </Card>

            {/* Thanh nút bấm chức năng */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "#fff",
                    padding: "16px 24px",
                    borderRadius: 16,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
            >
                <Button
                    icon={<FileAddFilled />}
                    onClick={handleDownloadFile}
                    style={{ height: 40, borderRadius: 8 }}
                >
                    ファイルをダウンロード {/* Tải file */}
                </Button>

                <Space size={16}>
                    <Text style={{ color: "#DC2626", fontSize: 18, fontWeight: "bold" }}>
                        合計: ¥{totalAmount.toLocaleString()} {/* Tổng: */}
                    </Text>
                    <div style={{ width: 120 }}>
                        <ButtonCustom
                            title="保存" /* Lưu */
                            backgroundColor="#F1F5F9"
                            color="#0F172A"
                            handlePress={handleSave}
                        />
                    </div>
                    <div style={{ width: 140 }}>
                        <ButtonCustom
                            title="注文送信" /* Gửi đơn hàng */
                            backgroundColor="#2563EB"
                            handlePress={handleSendOrder}
                        />
                    </div>
                </Space>
            </div>
        </div>
    );
};